import { Injectable } from '@angular/core';
import { ContractListItem } from '@app/features/contracts/contract.model';
import {
  ValidationAction,
  ValidationActionType,
} from '@app/features/contracts/validation/validation-action.model';
import { ValidationRequestTypeLabelPipe } from '@app/features/contracts/validation/validation-request-type-label-pipe';
import { ValidationRequest } from '@app/features/contracts/validation/validation-request.model';
import { Observable, of } from 'rxjs';
import {
  ContractHistorySource,
  HistoryEntry,
  HistoryOrigin,
  HistorySubject,
  HistoryText,
} from './history-entry.model';

export const VALIDATION_ORIGIN: HistoryOrigin = { key: 'validation', label: 'Validation' };

const typeLabel = new ValidationRequestTypeLabelPipe();

const gestureLabels: Record<ValidationActionType, string> = {
  REQUESTED: 'demandée',
  APPROVED: 'acceptée',
  REFUSED: 'refusée',
  CANCELLED: 'annulée',
  COMMENTED: 'commentée',
};

/**
 * Les entrées des gestes de ces demandes de validation — demande, acceptation, refus, annulation,
 * commentaire —, marquées du sujet qu'elles portent quand ce n'est pas l'embauche.
 */
export function validationEntries(
  requests: readonly ValidationRequest[],
  subject?: HistorySubject,
): HistoryEntry[] {
  return requests.flatMap((request) =>
    gestures(request).map((action) => entry(request, action, subject)),
  );
}

function gestures(request: ValidationRequest): ValidationAction[] {
  const actions = request.validationActions ?? [];
  // Une demande dont la création n'a pas été consignée en geste reste datée de sa création.
  const requested: ValidationAction[] = actions.some((a) => a.actionType === 'REQUESTED')
    ? []
    : [
        {
          id: 'creation',
          actionType: 'REQUESTED',
          performedBy: request.requestedBy,
          createdAt: request.createdAt,
        },
      ];
  return [...requested, ...actions];
}

function entry(
  request: ValidationRequest,
  action: ValidationAction,
  subject: HistorySubject | undefined,
): HistoryEntry {
  const gesture = `Validation ${typeLabel.transform(request.type)} ${gestureLabels[action.actionType]} par `;
  const author = { userId: action.performedBy ?? null };
  const recipient = { userId: request.requestedTo ?? null };
  const isRequest = action.actionType === 'REQUESTED';
  const title: HistoryText = isRequest ? [gesture, author, ' à ', recipient] : [gesture, author];

  return {
    id: `validation-${request.id}-${action.id}`,
    origin: VALIDATION_ORIGIN,
    ...(subject ? { subject } : {}),
    at: action.createdAt,
    title,
    lines: isRequest ? [] : [{ text: ['Demande adressée à ', recipient] }],
    comment: action.comment ?? null,
  };
}

/** Les gestes des demandes de validation de l'embauche ; ceux d'un avenant viennent avec lui. */
@Injectable()
export class ValidationHistorySource implements ContractHistorySource {
  readonly origin = VALIDATION_ORIGIN;

  entries(contract: ContractListItem): Observable<HistoryEntry[]> {
    return of(validationEntries(contract.validationRequests ?? []));
  }
}
